import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";
import { VideoThumbnailWithFallback } from "@/components/ui/VideoThumbnailWithFallback";
import { InlineVideoPlayer } from "@/components/ui/InlineVideoPlayer";
import { cn } from "@/utils/cn";

interface MediaDisplayProps {
  mediaItem: MediaItem;
  useThumbnail?: boolean;
  showPlayButton?: boolean;
  autoPlay?: boolean;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  onClick?: (e: React.MouseEvent) => void;
}

export const MediaDisplay: React.FC<MediaDisplayProps> = ({
  mediaItem,
  useThumbnail = false,
  showPlayButton = true,
  autoPlay = false,
  className,
  onLoad,
  onError,
  onClick,
}) => {

  // Video rendering
  if (mediaItem.isVideo()) {
    const isReady = mediaItem.isReadyForDisplay();

    if (!isReady) {
      return (
        <div className={cn("w-full h-full flex items-center justify-center bg-gray-50", className)}>
          <div className="text-center">
            {mediaItem.processingStatus === "processing" ? (
              <>
                <div className="w-6 h-6 mx-auto mb-2">
                  <div className="w-full h-full border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                </div>
                <p className="text-xs text-gray-600">Processing...</p>
              </>
            ) : (
              <>
                <div className="w-6 h-6 text-gray-400 mx-auto mb-2">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-xs text-gray-500">Pending</p>
              </>
            )}
          </div>
        </div>
      );
    }

    if (autoPlay) {
      return (
        <InlineVideoPlayer
          mediaItem={mediaItem}
          autoPlay
          loop
          muted
          objectFit="cover"
          className={cn("w-full h-full", className)}
        />
      );
    }

    return (
      <VideoThumbnailWithFallback
        mediaItem={mediaItem}
        showPlayButton={showPlayButton}
        className={cn("w-full h-full", className)}
        onClick={onClick}
      />
    );
  }

  // Image rendering
  if (mediaItem.isImage()) {
    return (
      <div
        onClick={onClick}
        className={cn("w-full h-full", onClick && "cursor-pointer")}
      >
        <UnifiedAuthenticatedImage
          mediaItem={mediaItem}
          alt={mediaItem.getName()}
          className={cn("w-full h-full object-cover", className)}
          useThumbnail={useThumbnail}
        />
      </div>
    );
  }

  // Fallback for unknown media types
  return (
    <div className={cn("w-full h-full flex items-center justify-center bg-gray-100", className)}>
      <div className="text-center">
        <div className="w-8 h-8 text-gray-400 mx-auto mb-2">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        </div>
        <p className="text-xs text-gray-500">Unknown media type</p>
      </div>
    </div>
  );
};
